import { ModelMergerComponentModule, ModelMergerField } from '@b2b/common/components/model-merger';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { UIBModal, createUIBModalStub, input } from '@b2b/types/core';
import { Customer } from '@b2b/types/customer';
import { StateService } from '@uirouter/angularjs';
import angular from 'angular';
import invariant from 'tiny-invariant';
import { createActor } from 'xstate';
import { customersIndividualsModalsMergeMachine } from '../machines/customers-individuals-modals-merge.machine';
import {
  MergeCustomersModalService,
  MergeCustomersModalServiceModule,
} from '../services/merge-customers-modal.service';
import { CustomersIndividualsModalsMergeData } from '../types';

class CustomersIndividualsModalsMergeController {
  static $inject = ['$state', 'NoticeFactory', 'MergeCustomersModalService'];

  private actor = this.createActor();
  modalInstance = input.required<UIBModal<Customer>>(createUIBModalStub<any>(undefined));
  resolve = input.required<{ MODAL_DATA: CustomersIndividualsModalsMergeData }>({
    MODAL_DATA: { customersDetails: [] },
  });

  notice = new this.noticeFactory();
  candidates: Customer[] = [];
  model: Partial<Customer> = {};
  modelMappers = {
    getId: (customer: Customer) => customer.customerId,
    getColumnHeader: (customer: Customer) => [customer.firstName, customer.lastName].filter(Boolean).join(' '),
    getColumnSubHeader: (customer: Customer) => {
      const href = this.$state.href('application.customers.individuals.detail', { id: customer.id });
      let template = '';

      if (customer.memberships.length) {
        if (customer.memberships.length > 1) {
          template = `
            <div>
              <span class="acme-model-merger__block"><i>Multiple Memberships for customer</i></span>
              <span class="acme-model-merger__block">#<a 
                href="${href}" target="_blank">${customer.customerId}</a></span>
            </div>
          `;
        } else {
          template = `
            <div>
              <span class="acme-model-merger__block">Member since ${
                customer.memberships[0].membershipJoinedDateParsed
              }</span>
              <span class="acme-model-merger__block">#<a href="${href}" target="_blank">${
                customer.memberships[0].acmeMembershipNumber
              }</a>  <i 
                class="acme-model-merger__caps">(${customer.memberships[0].membershipStanding})</i></span>
              <span class="acme-model-merger__block">
                ${customer.memberships[0].auxiliaryMembership ? 'Aux: ' : ''} 
                ${customer.memberships[0].membershipCategory || ''}</span>
              <span class="acme-model-merger__block">
                ${
                  customer.memberships[0].membershipStanding === 'new'
                    ? 'Starts ' + customer.memberships[0].membershipStartDateParsed
                    : 'Exp ' + customer.memberships[0].membershipExpirationDateParsed
                }
            </div>
          `;
        }
      } else {
        template = `<span class="acme-model-merger__empty"><i>not a member</i></span>`;
      }

      if (customer.orgName) {
        template += `
          <div>
            <span class="acme-model-merger__block">Org: ${customer.orgName}</span>
          </div>
        `;
      }

      return template;
    },
  };
  fields: ModelMergerField<Customer>[] = [
    {
      key: 'customerId',
      label: 'ACME Customer Number',
      selectable: true,
      onChange: (event) => {
        this.model.externalCustomerImportId = event.candidate.externalCustomerImportId;
      },
    },
    { key: 'externalCustomerImportId', label: 'External Import ID', selectable: false },
    { key: 'title', label: 'Title', selectable: true },
    { key: 'firstName', label: 'First Name', selectable: true },
    { key: 'middleName', label: 'Middle', selectable: true },
    { key: 'lastName', label: 'Last Name', selectable: true },
    { key: 'email', label: 'Email', selectable: true },
    { key: 'phoneNumber', label: 'Phone', selectable: true },
    {
      key: 'address',
      label: 'Address',
      selectable: true,
      fields: [
        { key: 'address.0.streetAddress1', label: 'Street Address 1' },
        { key: 'address.0.streetAddress2', label: 'Street Address 2' },
        { key: 'address.0.city', label: 'City' },
        { key: 'address.0.state', label: 'State' },
        { key: 'address.0.zipCode', label: 'Zip/Postal Code' },
        { key: 'address.0.country', label: 'Country' },
      ],
    },
  ];

  get snapshot() {
    return this.actor.getSnapshot();
  }

  constructor(
    private $state: StateService,
    private noticeFactory: NoticeFactory,
    private mergeCustomersModalService: MergeCustomersModalService,
  ) {}

  $onInit() {
    this.candidates = this.resolve.MODAL_DATA.customersDetails;
  }

  $onDestroy() {
    this.actor.stop();
  }

  back() {
    this.actor.send({ type: 'BACK' });
  }

  review() {
    this.actor.send({ type: 'REVIEW' });
  }

  submit() {
    const base = this.candidates.find((candidate) => candidate.customerId === this.model.customerId);
    const duplicates = this.candidates.filter((candidate) => candidate.customerId !== this.model.customerId);

    invariant(base);

    const updatedCustomer = {
      ...angular.copy(base),
      ...this.model,
    };

    this.actor.send({ type: 'MERGE_CUSTOMERS' });

    return this.mergeCustomersModalService
      .updateCustomer(updatedCustomer)
      .then((updateCustomerResponse) => {
        return this.mergeCustomersModalService
          .mergeCustomers({
            baseId: base.id,
            duplicateIds: duplicates.map((customer) => customer.id),
          })
          .then(() => {
            return updateCustomerResponse.payload;
          });
      })
      .then((customer) => {
        this.actor.send({ type: 'MERGE_CUSTOMERS_SUCCESS', result: customer });
      })
      .catch((error: any) => {
        this.notice.showErrorMessages([{ message: error?.data?.error ?? 'An unknown error has occurred' }], {
          duration: 5000,
        });
        this.actor.send({ type: 'MERGE_CUSTOMERS_FAILED' });
      });
  }

  cancel() {
    this.actor.send({ type: 'CANCEL' });
  }

  private createActor() {
    return createActor(
      customersIndividualsModalsMergeMachine.provide({
        actions: {
          dismissModal: () => {
            this.modalInstance.dismiss();
          },
          doneModal: (_, props: { result: Customer }) => {
            this.modalInstance.close(props.result);
          },
        },
      }),
    ).start();
  }
}

export const CustomersIndividualsModalsMergeComponentModule = angular
  .module('acme.view.application.customers.modals.merge.controllers', [
    'ui.bootstrap',
    'acme.common.factory.notice',
    MergeCustomersModalServiceModule,
    NoticesComponentModule,
    ModelMergerComponentModule,
  ])
  .component('acmeCustomersIndividualsModalsMerge', {
    templateUrl:
      'views/application/views/customers/modals/merge/components/customers-individuals-modals-merge.component.tpl.html',
    controller: CustomersIndividualsModalsMergeController,
    bindings: {
      modalInstance: '<',
      resolve: '<',
    },
  }).name;
